import { createNativeStackNavigator } from '@react-navigation/native-stack'
import { ActionsListPage } from 'components/pages/ActionsList'
import { AuthWelcomePage } from 'components/pages/AuthWelcome'
import { Chat } from 'components/pages/Chat'
import { ChatRoomPage } from 'components/pages/ChatRoom'
import { EditProgramPage } from 'components/pages/EditProgram'
import { LoginPage } from 'components/pages/Login'
import { PostPage } from 'components/pages/Post'
import { PostEditorPage } from 'components/pages/PostEditor'
import { ProgramPage } from 'components/pages/Program'
import { ResetPasswordPage } from 'components/pages/ResetPassword'
import { SignupPage } from 'components/pages/Signup'
import { UpscorePage } from 'components/pages/Upscore'
import { WorkoutEditorPage } from 'components/pages/WorkoutEditor'
import React from 'react'

import * as constants from 'utils/constants'
import { links } from 'utils/constants'
import { postUser } from 'utils/queries'

import { UserType } from 'types/user'

import { MainTabs } from './MainTabs'
import { OnboardingStack } from './OnboardingStack'

export type StackParamList = {
  [constants.links.MAIN_TABS]: undefined
  [constants.links.POST_PAGE]: undefined
  [constants.links.SHARE_GROUP_PAGE]: undefined
  [constants.links.EDIT_WORKOUT_PAGE]: undefined
  [constants.links.CHAT_PAGE]: undefined
  [constants.links.CHAT_ROOM]: undefined
  [constants.links.CREATE_WORKOUT_PAGE]: undefined
  [constants.links.CREATE_POST_PAGE]: undefined
}

export const Stack = createNativeStackNavigator<StackParamList>()

export const updatePushNotificationTokensForUser = async (
  currentUser: UserType,
  currentToken: string,
) => {
  if (!currentUser.pushNotificationTokens) {
    // eslint-disable-next-line no-param-reassign
    currentUser.pushNotificationTokens = [currentToken]
    await postUser({
      userId: currentUser.id,
      updatedOrCreatedData: {
        pushNotificationTokens: currentUser.pushNotificationTokens,
      },
    })
  }

  if (
    currentUser.pushNotificationTokens &&
    !currentUser.pushNotificationTokens.find((pnt) => pnt === currentToken)
  ) {
    currentUser.pushNotificationTokens.push(currentToken)
    await postUser({
      userId: currentUser.id,
      updatedOrCreatedData: {
        pushNotificationTokens: currentUser.pushNotificationTokens,
      },
    })
  }
}

export type RootStackParamList = {
  // Authenticated Routes
  [links.CLIENT_STACK]: undefined

  // Unauthenticated Routes
  [links.AUTH_WELCOME_PAGE]: undefined
  [links.SIGNUP_PAGE]: undefined
  [links.LOGIN_PAGE]: undefined
  [links.RESET_PASSWORD_PAGE]: undefined
}

export const computeCurrentRoutes = (
  RootStackNavigator: unknown,
  meta: { onboardingComplete: boolean; loggedIn: boolean },
) => {
  const onboardNotCompleteRoutes = (
    <>
      <RootStackNavigator.Screen
        name={links.ONBOARDING_STACK}
        component={OnboardingStack}
        options={{
          gestureEnabled: true,
          headerShown: false,
        }}
      />
    </>
  )

  const onboardCompleteRoutes = (
    <>
      <RootStackNavigator.Screen
        name={links.MAIN_TABS}
        component={MainTabs}
        options={{
          headerShown: false,
        }}
      />

      <RootStackNavigator.Screen
        name={links.POST_PAGE}
        component={PostPage}
        options={{ title: 'Post', headerShown: false }}
      />

      <RootStackNavigator.Screen
        name={links.UPSCORE_PAGE}
        component={UpscorePage}
        options={{ title: 'Upscore', headerShown: false }}
      />

      <RootStackNavigator.Screen name={links.CHAT_PAGE} component={Chat} />

      <RootStackNavigator.Screen
        name={links.CHAT_ROOM}
        component={ChatRoomPage}
        options={{ headerShown: false }}
      />

      <RootStackNavigator.Screen
        options={{
          headerShown: false,
          title: 'Create Post',
        }}
        name={links.CREATE_WORKOUT_PAGE}
        component={WorkoutEditorPage}
      />

      <RootStackNavigator.Screen
        options={{
          headerShown: false,
          title: 'Edit Post',
        }}
        component={WorkoutEditorPage}
        name={links.EDIT_WORKOUT_PAGE}
      />

      <RootStackNavigator.Screen
        options={{
          headerShown: false,
          title: 'Start Post',
        }}
        component={PostEditorPage}
        name={links.CREATE_POST_PAGE}
      />

      <RootStackNavigator.Screen
        options={{
          headerShown: false,
          title: '',
        }}
        component={ProgramPage}
        name={links.PROGRAM_PAGE}
      />

      <RootStackNavigator.Screen
        options={{
          headerShown: false,
          title: '',
        }}
        component={EditProgramPage}
        name={links.EDIT_PROGRAM_PAGE}
      />

      <RootStackNavigator.Screen
        options={{
          headerShown: false,
          title: '',
        }}
        component={ActionsListPage}
        name={links.ACTIONS_LIST_PAGE}
      />
    </>
  )

  let loggedInRoutes
  if (!meta.onboardingComplete) {
    loggedInRoutes = onboardNotCompleteRoutes
  } else {
    loggedInRoutes = onboardCompleteRoutes
  }

  const loggedOutRoutes = (
    <>
      <RootStackNavigator.Screen
        // the name of this is reused in the auth routes as well
        name={links.AUTH_WELCOME_PAGE}
        component={AuthWelcomePage}
        options={{
          title: 'Welcome',
          gestureEnabled: true,
          headerShown: false,
        }}
      />

      <RootStackNavigator.Screen
        name={links.SIGNUP_PAGE}
        component={SignupPage}
        options={{
          title: 'Sign up',
          gestureEnabled: true,
          headerShown: false,
        }}
      />

      <RootStackNavigator.Screen
        name={links.LOGIN_PAGE}
        component={LoginPage}
        options={{
          title: 'Login',
          gestureEnabled: true,
          headerShown: false,
        }}
      />

      <RootStackNavigator.Screen
        name={links.RESET_PASSWORD_PAGE}
        component={ResetPasswordPage}
        options={{
          title: 'Reset Password',
          gestureEnabled: true,
          headerShown: false,
        }}
      />
    </>
  )

  let currentRoutes
  if (meta.loggedIn) {
    currentRoutes = loggedInRoutes
  } else {
    currentRoutes = loggedOutRoutes
  }

  return currentRoutes
}
